import { ComponentProps, FC, useCallback } from 'react';
import MUIPagination from '@mui/material/Pagination';
import Theme from 'App.theme';

export type PaginationPageChangeEvent = {
  id: string;
  page: number;
};

export type PageChangeHandler = (page: number) => void;

type Props = {
  id?: string;
  numPages?: number;
  curPage?: number;
  disabled?: boolean;
  onChange?: PageChangeHandler;
} & Omit<ComponentProps<typeof MUIPagination>, 'onChange'>;

const Pagination: FC<Props> = ({ id = '', numPages = 1, curPage = 1, disabled, onChange, sx, ...props }) => {
  const handleChange = useCallback(
    (_evt: any, page: number) => {
      // if an onChange handler is passed call it
      if (onChange) onChange(page);
      // else emit an event
      else {
        const detail = { page, id };
        const event = new CustomEvent<PaginationPageChangeEvent>('onPaginationPageChange', { detail });
        document.dispatchEvent(event);
      }
    },
    [onChange, id],
  );

  return (
    <MUIPagination
      count={numPages}
      page={curPage}
      variant="outlined"
      shape="rounded"
      disabled={disabled}
      onChange={handleChange}
      sx={{
        alignSelf: 'baseline',
        margin: `auto auto ${Theme.layout.spacing.medium}`,

        '& .MuiPaginationItem-previousNext': {
          color: Theme.colours.active,
          borderWidth: 0,
        },

        '& .MuiPaginationItem-page': {
          color: Theme.colours.active,
          borderColor: Theme.colours.active,

          '&.Mui-selected, &:hover': {
            backgroundColor: Theme.colours.active + ' !important',
            color: Theme.colours.white,
          },
        },

        '& button:not(.Mui-disabled, .Mui-selected):hover': {
          backgroundColor: Theme.colours.activeLight,
        },

        ...sx,
      }}
      {...props}
    />
  );
};

export default Pagination;
